import React from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    Button,
    CircularProgress,
    Tooltip,
} from "@mui/material";
import { PrecisionManufacturing as EquipmentIcon, ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { formatDate, periodLabel } from "./period";

export default function EquipmentList({
    equipmentList,
    loading,
    cadence,
    isChecked,
    lastInspectionFor,
    openIssueFor,
    onStartCheck,
    onOpenDetails,
    onResolveIssue,
}) {
    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (equipmentList.length === 0) {
        return (
            <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                <EquipmentIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                <Typography variant="body2">No equipment found for this type.</Typography>
            </Box>
        );
    }

    const period = periodLabel(cadence);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {equipmentList.map((eq) => {
                const checked = isChecked(eq.id);
                const lastInspection = lastInspectionFor(eq.id);
                const openIssue = openIssueFor(eq.id);
                return (
                    <Card key={eq.id} sx={{ boxShadow: "none", border: 1, borderColor: "primary.main" }}>
                        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                                <Box
                                    sx={{
                                        minWidth: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        cursor: onOpenDetails ? "pointer" : "default",
                                        borderRadius: 1,
                                        mx: -0.5,
                                        px: 0.5,
                                        "&:hover": onOpenDetails ? { backgroundColor: "action.hover" } : undefined,
                                    }}
                                    onClick={() => onOpenDetails?.(eq)}
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="subtitle2" fontWeight={700}>
                                            {eq.nickname}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            {[eq.make, eq.model, eq.year].filter(Boolean).join(" · ") || "—"}
                                        </Typography>
                                    </Box>
                                    {onOpenDetails && <ChevronRightIcon fontSize="small" color="action" />}
                                </Box>
                                {checked && period && (
                                    <Chip label={`Checked ${period}`} size="small" color="success" />
                                )}
                            </Box>

                            <Box sx={{ display: "flex", gap: 0.5, mt: 1, flexWrap: "wrap", alignItems: "center" }}>
                                {lastInspection && (
                                    <Chip
                                        label={`Last check ${formatDate(lastInspection.date)}${
                                            lastInspection.created_by_detail
                                                ? ` · ${lastInspection.created_by_detail.full_name || lastInspection.created_by_detail.username}`
                                                : ""
                                        }`}
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                                {openIssue && (
                                    <Chip
                                        label="Open issue"
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        onClick={() => onResolveIssue?.(eq, openIssue)}
                                    />
                                )}
                            </Box>

                            <Box sx={{ mt: 1.5 }}>
                                <Tooltip title={openIssue ? "Resolve the open issue before checking again." : ""}>
                                    <span>
                                        <Button
                                            size="small"
                                            variant={checked ? "outlined" : "contained"}
                                            disabled={Boolean(openIssue)}
                                            onClick={() => onStartCheck(eq)}
                                        >
                                            {checked ? "Re-check" : "Start Check"}
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                        </CardContent>
                    </Card>
                );
            })}
        </Box>
    );
}
